import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { RadioButton } from "primereact/radiobutton";
import {
  AssetExportColumn,
  AssetExportField,
  AssetExportRowMode,
  AssetExportScope,
  assetScraperExportApi,
} from "../../../api/assetScraperExportApi";

interface CustomAssetExportDialogProps {
  jobId: string | null;
  jobTitle?: string;
  visible: boolean;
  onHide: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const FIELD_OPTIONS: Array<{ label: string; value: AssetExportField }> = [
  { label: "Asset ID", value: "asset_id" },
  { label: "Asset type", value: "asset_type" },
  { label: "Title", value: "title" },
  { label: "Artist", value: "artist" },
  { label: "ISRC", value: "isrc" },
  { label: "UPC", value: "upc" },
  { label: "Record label", value: "label" },
  { label: "Writers", value: "writer" },
  { label: "Custom ID", value: "custom_id" },
  { label: "Description", value: "description" },
  { label: "Asset labels", value: "labels" },
  { label: "Parent asset ID", value: "parent_asset_id" },
  { label: "Primary child ID", value: "child_asset" },
  { label: "Created time", value: "time_created" },
  { label: "Updated time", value: "time_updated" },
];

const ASSET_DEFAULTS: AssetExportField[] = [
  "asset_id",
  "asset_type",
  "title",
  "artist",
  "isrc",
  "upc",
  "label",
];

const HIERARCHY_DEFAULTS: Record<
  Exclude<AssetExportScope, "asset">,
  AssetExportField[]
> = {
  root: ["asset_id", "asset_type", "title"],
  child: ["asset_id", "asset_type", "title", "artist", "isrc", "upc", "label"],
  grandchild: ["asset_id", "asset_type", "title", "writer"],
};

const SCOPE_LABELS: Record<Exclude<AssetExportScope, "asset">, string> = {
  root: "Root asset",
  child: "Child asset",
  grandchild: "Grandchild asset",
};

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

const CustomAssetExportDialog = ({
  jobId,
  jobTitle,
  visible,
  onHide,
  onSuccess,
  onError,
}: CustomAssetExportDialogProps) => {
  const [rowMode, setRowMode] = useState<AssetExportRowMode>("hierarchy");
  const [assetFields, setAssetFields] =
    useState<AssetExportField[]>(ASSET_DEFAULTS);
  const [hierarchyFields, setHierarchyFields] = useState(HIERARCHY_DEFAULTS);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setRowMode("hierarchy");
    setAssetFields(ASSET_DEFAULTS);
    setHierarchyFields(HIERARCHY_DEFAULTS);
  }, [visible, jobId]);

  const columns = useMemo<AssetExportColumn[]>(() => {
    if (rowMode === "assets") {
      return assetFields.map((field) => ({ scope: "asset", field }));
    }
    return (["root", "child", "grandchild"] as const).flatMap((scope) =>
      hierarchyFields[scope].map((field) => ({ scope, field }))
    );
  }, [assetFields, hierarchyFields, rowMode]);

  const handleDownload = async () => {
    if (!jobId || columns.length === 0) {
      onError("Select at least one column");
      return;
    }
    try {
      setDownloading(true);
      const result = await assetScraperExportApi.download(jobId, {
        rowMode,
        columns,
      });
      triggerBlobDownload(result.blob, result.filename);
      onSuccess(`Custom CSV downloaded with ${columns.length} columns`);
      onHide();
    } catch (error) {
      console.error("Custom asset export failed:", error);
      onError("Failed to generate custom CSV export");
    } finally {
      setDownloading(false);
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={downloading}
      />
      <Button
        label={`Download ${columns.length} columns`}
        icon="pi pi-download"
        onClick={handleDownload}
        loading={downloading}
        disabled={!jobId || columns.length === 0}
      />
    </div>
  );

  return (
    <Dialog
      header={`Custom CSV export${jobTitle ? ` — ${jobTitle}` : ""}`}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "min(760px, 95vw)" }}
      modal
    >
      <div className="flex flex-column gap-4">
        <div>
          <div className="font-semibold mb-2">Row layout</div>
          <div className="flex flex-column gap-2">
            <label className="flex align-items-center gap-2 cursor-pointer">
              <RadioButton
                inputId="export-hierarchy"
                value="hierarchy"
                checked={rowMode === "hierarchy"}
                onChange={() => setRowMode("hierarchy")}
              />
              <span>
                <strong>Parent/child hierarchy</strong> — flatten root → child →
                grandchild onto one row per relationship path
              </span>
            </label>
            <label className="flex align-items-center gap-2 cursor-pointer">
              <RadioButton
                inputId="export-assets"
                value="assets"
                checked={rowMode === "assets"}
                onChange={() => setRowMode("assets")}
              />
              <span>
                <strong>Individual assets</strong> — one row per scraped asset
              </span>
            </label>
          </div>
        </div>

        {rowMode === "assets" ? (
          <div>
            <label className="font-semibold block mb-2">Asset columns</label>
            <MultiSelect
              value={assetFields}
              options={FIELD_OPTIONS}
              onChange={(event) => setAssetFields(event.value)}
              display="chip"
              filter
              placeholder="Select columns"
              className="w-full"
            />
          </div>
        ) : (
          <div className="flex flex-column gap-3">
            <small className="text-color-secondary">
              Select fields independently at each level. Empty child levels are
              retained, and multiple children produce multiple rows.
            </small>
            {(["root", "child", "grandchild"] as const).map((scope) => (
              <div key={scope}>
                <label className="font-semibold block mb-2">
                  {SCOPE_LABELS[scope]} columns
                </label>
                <MultiSelect
                  value={hierarchyFields[scope]}
                  options={FIELD_OPTIONS}
                  onChange={(event) =>
                    setHierarchyFields((current) => ({
                      ...current,
                      [scope]: event.value,
                    }))
                  }
                  display="chip"
                  filter
                  placeholder={`Select ${SCOPE_LABELS[scope].toLowerCase()} columns`}
                  className="w-full"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default CustomAssetExportDialog;
